/**
 * Debug Utility for Selective Logging
 * 
 * Provides structured logging with environment variable controls for different subsystems.
 * Enable specific logging categories by setting corresponding environment variables to 'true'.
 */

// Test log to verify file is being loaded
console.log('🚀 DEBUG UTIL LOADED - File is being imported correctly');

// Get environment variables with fallback
const getEnv = (key) => {
  // Try import.meta.env first (Vite standard)
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    return import.meta.env[key];
  }
  // Fallback to process.env if available
  if (typeof process !== 'undefined' && process.env) {
    return process.env[key];
  }
  return undefined;
};

const NODE_ENV = getEnv('NODE_ENV');
const VITE_DEBUG_ARCHITECTURE = getEnv('VITE_DEBUG_ARCHITECTURE');
const VITE_DEBUG_MULTISELECT = getEnv('VITE_DEBUG_MULTISELECT');
const VITE_DEBUG_CONTEXT = getEnv('VITE_DEBUG_CONTEXT');
const VITE_DEBUG_SUBFLOW = getEnv('VITE_DEBUG_SUBFLOW');
const VITE_DEBUG_UI_SYNC = getEnv('VITE_DEBUG_UI_SYNC');

console.log('🔍 Environment check:', {
  NODE_ENV,
  VITE_DEBUG_ARCHITECTURE,
  VITE_DEBUG_MULTISELECT,
  VITE_DEBUG_CONTEXT,
  VITE_DEBUG_SUBFLOW,
  VITE_DEBUG_UI_SYNC
});

// Debug categories - each can be controlled independently
const DEBUG_CATEGORIES = {
  ARCHITECTURE: VITE_DEBUG_ARCHITECTURE === 'true',
  MULTISELECT: VITE_DEBUG_MULTISELECT === 'true', 
  CONTEXT: VITE_DEBUG_CONTEXT === 'true',
  SUBFLOW: VITE_DEBUG_SUBFLOW === 'true',
  UI_SYNC: VITE_DEBUG_UI_SYNC === 'true'
};

// Only enable in development mode OR when debug flags are explicitly set
const isDevelopment = NODE_ENV === 'development' || Object.values(DEBUG_CATEGORIES).some(enabled => enabled);

/**
 * Main debug logging function
 * @param {string} category - Debug category (ARCHITECTURE, MULTISELECT, CONTEXT, SUBFLOW, UI_SYNC)
 * @param {string} prefix - Emoji/prefix for log identification
 * @param {...any} args - Arguments to log
 */
export const debugLog = (category, prefix, ...args) => {
  if (DEBUG_CATEGORIES[category]) {
    console.log(prefix, ...args);
  }
};

/**
 * Category-specific logging functions for convenience
 */
export const debugArchitecture = (prefix, ...args) => debugLog('ARCHITECTURE', prefix, ...args);
export const debugMultiselect = (prefix, ...args) => debugLog('MULTISELECT', prefix, ...args);
export const debugContext = (prefix, ...args) => debugLog('CONTEXT', prefix, ...args);
export const debugSubflow = (prefix, ...args) => debugLog('SUBFLOW', prefix, ...args);
export const debugUISync = (prefix, ...args) => debugLog('UI_SYNC', prefix, ...args);

/**
 * Get current debug configuration status
 */
export const getDebugStatus = () => ({
  isDevelopment,
  categories: DEBUG_CATEGORIES,
  anyEnabled: Object.values(DEBUG_CATEGORIES).some(enabled => enabled)
});

/**
 * Log debug configuration on import (only if any category is enabled)
 */
if (isDevelopment) {
  // Always log environment variable status for debugging
  console.log('🔧 Final Environment Variables:', {
    NODE_ENV,
    VITE_DEBUG_ARCHITECTURE,
    VITE_DEBUG_MULTISELECT,
    VITE_DEBUG_CONTEXT,
    VITE_DEBUG_SUBFLOW,
    VITE_DEBUG_UI_SYNC,
  });
  
  if (Object.values(DEBUG_CATEGORIES).some(enabled => enabled)) {
    console.log('🔧 Debug Configuration:', {
      categories: Object.entries(DEBUG_CATEGORIES)
        .filter(([_, enabled]) => enabled)
        .map(([category, _]) => category),
      environment: 'development'
    });
  } else {
    console.log('🔧 No debug categories enabled - all environment variables are false or undefined');
  }
}